/* eslint-disable react-perf/jsx-no-new-object-as-prop */
/* eslint-disable react-perf/jsx-no-new-function-as-prop */

import { ReactNode, useCallback, useContext } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { NavigationTitle } from '@cbhq/cds-web/navigation/NavigationTitle';
import { NavigationBar } from '@cbhq/identity-navigation';
import { TwoFactorBoundaryError } from '@cbhq/two-factor-web/types';

import { Container } from ':support/components/Container';
import { getStaticConfig } from ':support/config';
import useIsMobile from ':support/hooks/useIsMobile';
import { useDynamicConfig } from ':support/providers/DynamicConfigProvider';
import { SessionContext } from ':support/providers/SessionProvider';
import { SpectrumContext } from ':support/providers/SpectrumProvider';
import { logError } from ':support/utils/error-vitals/logging';
import { getTwoFactorTargetEnv } from ':support/utils/getTargetEnv';

import { AIChatWidget } from './AIChatWidget/AIChatWidget';
import { AIModelProvider } from './AIChatWidget/AIModelProvider';

// Dynamically import TwoFactorGlobalConfigProvider with SSR disabled
const TwoFactorGlobalConfigProvider = dynamic(
  async () => import('@cbhq/two-factor-web').then((mod) => mod.TwoFactorGlobalConfigProvider),
  { ssr: false },
);

type GlobalWrapperProps = {
  children: ReactNode;
  locale: string;
};

const headerStyles = { cursor: 'pointer' };

export function GlobalWrapper({ children }: GlobalWrapperProps) {
  const router = useRouter();
  const { navBarAvailable } = useContext(SessionContext);
  const { spectrum, setSpectrum } = useContext(SpectrumContext);
  const isMobile = useIsMobile();
  const { showAIAssistant } = useDynamicConfig();

  const handleHeaderClick = useCallback(() => {
    void router.push('/');
  }, [router]);

  const { twoFactorClientId, authEnvironment, appSwitcherProjectId } = getStaticConfig();

  const handleBoundaryError = useCallback((boundaryError: TwoFactorBoundaryError) => {
    const { error, errorInfo, context, details } = boundaryError;

    logError(
      error.message,
      {
        context,
      },
      {
        name: 'API Key Creation Two Factor Error',
        error: error.message,
        errorInfo: errorInfo.componentStack,
        details: details?.toString(),
      },
    );
  }, []);

  const handleToggleSpectrum = useCallback(() => {
    const newSpectrum = spectrum === 'light' ? 'dark' : 'light';
    setSpectrum(newSpectrum);

    const htmlElement = document.documentElement;
    htmlElement?.setAttribute('data-theme', newSpectrum);
  }, [spectrum, setSpectrum]);

  if (!twoFactorClientId) {
    return <div />;
  }

  return (
    <TwoFactorGlobalConfigProvider
      clientId={twoFactorClientId}
      target={getTwoFactorTargetEnv(authEnvironment)}
      onBoundaryError={handleBoundaryError}
    >
      {!navBarAvailable ? null : (
        <VStack
          background="bg"
          borderedBottom
          width="100%"
          style={
            isMobile ? { zIndex: 1000, position: 'fixed', top: 0, left: 0, width: '100vw' } : {}
          }
        >
          <Container hideSideMenu id="navbar-container">
            <NavigationBar
              action="web-Eaa-CdpSupport-SignedInNavbar"
              projectId={appSwitcherProjectId}
              target={getTwoFactorTargetEnv(authEnvironment)}
              themeToggleValue={spectrum}
              onThemeToggle={handleToggleSpectrum}
              activeThemeLabel="Dark Mode"
              start={
                <HStack
                  as="div"
                  gap={1}
                  alignItems="center"
                  style={headerStyles}
                  onClick={handleHeaderClick}
                >
                  {isMobile ? (
                    <Image
                      src="/assets/images/coinbase-logo.svg"
                      alt="Coinbase Logo"
                      width={24}
                      height={24}
                    />
                  ) : (
                    <NavigationTitle>Developer Platform Support</NavigationTitle>
                  )}
                </HStack>
              }
              end={
                <HStack gap={1}>
                  {isMobile ? (
                    <IconButton
                      name="discordLogo"
                      accessibilityLabel="discord"
                      onClick={() => {
                        window.open(
                          'https://discord.com/channels/1220414409550336183/1220418962089836665',
                          '_blank',
                        );
                      }}
                    />
                  ) : (
                    <a
                      aria-label="link to CDP Discord Server"
                      href="https://discord.com/channels/1220414409550336183/1220418962089836665"
                      target="_blank"
                    >
                      <Button startIcon="discordLogo" variant="secondary" compact>
                        Discord
                      </Button>
                    </a>
                  )}
                </HStack>
              }
            >
              <div />
            </NavigationBar>
          </Container>
        </VStack>
      )}
      <VStack style={isMobile ? { position: 'relative', top: 65 } : {}}>{children}</VStack>
      {showAIAssistant && (
        <AIModelProvider>
          <AIChatWidget />
        </AIModelProvider>
      )}
    </TwoFactorGlobalConfigProvider>
  );
}
